import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Check,
    Copy,
    Search,
    X,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import Tooltip from '../ui/Tooltip';
import { ActiveFiltersStrip, FilterButton, FilterPanel, countActive, isFilterEmpty } from './filters';
import ColumnChooser from './columns/ColumnChooser';
import { chooserColumns, resolveColumns, revealedColumnKeys } from './columns/columnModel';
import useColumnPreference from '../../hooks/useColumnPreference';

const PAGE_SIZES = [10, 20, 50, 100];

/**
 * Server-paged data table. Pair with hooks/useServerTable and spread its `tableProps`.
 *
 * columns: [{ key, header, render?(row), sortKey?, className?, headerClassName?, align?,
 *            defaultVisible?, locked?, label?, filter? }]
 *   - sortKey must be a key the endpoint whitelists (lib/paginate.js `sortable`).
 *   - defaultVisible: false  the column starts hidden; the user adds it from "Columns".
 *   - locked: true           the column cannot be hidden or moved (name, row actions).
 *   - filter: { type, key?, label?, apiKey?, options?, ... } makes the column filterable; the
 *     page derives `filterFields` from it (useServerTable `columns` option), so the Filters
 *     panel and chips can only offer what the table has. A hidden column that is filtered on
 *     is shown automatically while the filter is applied.
 * tableKey: stable id of the table; enables the "Columns" menu and remembers the user's
 *   choice in this browser (localStorage `um_ui_cols:<tableKey>`). Omit for no menu.
 * filterFields / filterValues / onFilterChange / onClearFilters: the filter panel (a "Filters"
 *   button, a slide-out panel of filter cards and removable chips above the table). Supplied by
 *   useServerTable's tableProps when it is given `fields`.
 * filters: extra toolbar nodes, rendered after the Filters button.
 * toolbar: nodes rendered at the toolbar's right edge (e.g. bulk actions).
 */
const DataTable = ({
    columns: declaredColumns,
    tableKey,
    rows,
    rowKey,
    loading,
    error,
    sort,
    onSort,
    search,
    onSearchChange,
    searchPlaceholder = 'Search…',
    filters,
    toolbar,
    pagination,
    onPageChange,
    onLimitChange,
    hasActiveFilters,
    onResetFilters,
    emptyMessage = 'No records found.',
    rowClassName,
    filterFields,
    filterValues,
    onFilterChange,
    onClearFilters,
}) => {
    const [panel, setPanel] = useState({ open: false, focusKey: null });
    const filterButtonRef = useRef(null);
    const hasPanel = Array.isArray(filterFields) && filterFields.length > 0 && onFilterChange;
    // Preset fields (no column) show as chips only; the panel offers the columns' filters.
    const panelFields = hasPanel ? filterFields.filter((f) => !f.preset) : [];
    const values = filterValues || {};
    const prefs = useColumnPreference(tableKey, declaredColumns);
    const forced = hasPanel
        ? revealedColumnKeys(filterFields, (f) => !isFilterEmpty(f, values[f.key]))
        : [];
    const columns = prefs.enabled ? resolveColumns(declaredColumns, prefs.config, forced) : declaredColumns;
    const hiddenCount = prefs.enabled
        ? chooserColumns(declaredColumns).filter((c) => !columns.includes(c)).length
        : 0;
    const activeCount = countActive(panelFields, values);
    const openPanel = (focusKey = null) => setPanel({ open: true, focusKey });
    const closePanel = () => setPanel({ open: false, focusKey: null });
    const { page = 1, limit = 20, total = 0, totalPages = 0 } = pagination || {};
    const from = total === 0 ? 0 : (page - 1) * limit + 1;
    const to = Math.min(page * limit, total);
    const keyOf = (row, idx) => (typeof rowKey === 'function' ? rowKey(row) : row[rowKey]) ?? idx;

    return (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {(onSearchChange || filters || toolbar || hasPanel || prefs.enabled) && (
                <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50/60">
                    {onSearchChange && (
                        <div className="relative w-full sm:w-64">
                            <Search
                                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
                                aria-hidden="true"
                            />
                            <input
                                type="text"
                                role="searchbox"
                                value={search}
                                onChange={(e) => onSearchChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape' && search) {
                                        // Clear the box without also closing an enclosing modal.
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onSearchChange('');
                                    }
                                }}
                                placeholder={searchPlaceholder}
                                aria-label="Search"
                                className="h-9 w-full rounded-md border border-slate-200 bg-white pl-8 pr-8 text-sm shadow-sm outline-none placeholder:text-slate-400 focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-primary-200"
                            />
                            {search && (
                                <Tooltip label="Clear search">
                                    <button
                                        type="button"
                                        onClick={() => onSearchChange('')}
                                        aria-label="Clear search"
                                        className="absolute right-1.5 top-1/2 -translate-y-1/2 grid h-6 w-6 place-content-center rounded text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-primary-200"
                                    >
                                        <X className="h-3.5 w-3.5" aria-hidden="true" />
                                    </button>
                                </Tooltip>
                            )}
                        </div>
                    )}
                    {panelFields.length > 0 && (
                        <FilterButton
                            ref={filterButtonRef}
                            count={activeCount}
                            open={panel.open}
                            onClick={() => openPanel()}
                        />
                    )}
                    {prefs.enabled && (
                        <ColumnChooser
                            columns={declaredColumns}
                            config={prefs.config}
                            forced={forced}
                            hiddenCount={hiddenCount}
                            actions={prefs}
                        />
                    )}
                    {filters}
                    {toolbar && <div className="ml-auto flex items-center gap-2">{toolbar}</div>}
                </div>
            )}

            {hasPanel && (
                <>
                    <ActiveFiltersStrip
                        fields={filterFields}
                        values={values}
                        onRemove={(key) => onFilterChange(key, undefined)}
                        onClearAll={onClearFilters}
                        onEdit={(key) => openPanel(key)}
                    />
                    <FilterPanel
                        open={panel.open}
                        onClose={closePanel}
                        fields={panelFields}
                        values={values}
                        onChange={onFilterChange}
                        onClearAll={onClearFilters}
                        focusKey={panel.focusKey}
                        returnFocusRef={filterButtonRef}
                    />
                </>
            )}

            <div className="overflow-x-auto relative">
                <table className="w-full text-sm text-left text-slate-700">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                        <tr>
                            {columns.map((col) => {
                                const active = sort?.key && sort.key === col.sortKey;
                                const SortIcon = !active ? ArrowUpDown : sort.dir === 'desc' ? ArrowDown : ArrowUp;
                                return (
                                    <th
                                        key={col.key}
                                        scope="col"
                                        aria-sort={
                                            active ? (sort.dir === 'desc' ? 'descending' : 'ascending') : undefined
                                        }
                                        className={cn(
                                            'px-3 py-2 font-semibold whitespace-nowrap',
                                            col.align === 'right' && 'text-right',
                                            col.headerClassName
                                        )}
                                    >
                                        {col.sortKey && onSort ? (
                                            <button
                                                type="button"
                                                onClick={() => onSort(col.sortKey)}
                                                className={cn(
                                                    'inline-flex items-center gap-1 uppercase tracking-wide hover:text-slate-800',
                                                    active && 'text-slate-900'
                                                )}
                                            >
                                                {col.header}
                                                <SortIcon className={cn('h-3.5 w-3.5', !active && 'opacity-40')} />
                                            </button>
                                        ) : (
                                            col.header
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {error ? (
                            <tr>
                                <td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-red-600">
                                    {error}
                                </td>
                            </tr>
                        ) : loading && rows.length === 0 ? (
                            Array.from({ length: Math.min(limit, 8) }).map((_, i) => (
                                <tr key={`sk-${i}`} className="animate-pulse">
                                    {columns.map((col) => (
                                        <td key={col.key} className="px-3 py-2">
                                            <div className="h-3 rounded bg-slate-100" />
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-slate-500">
                                    {hasActiveFilters ? (
                                        <>
                                            No records match the current search and filters.
                                            {onResetFilters && (
                                                <>
                                                    {' '}
                                                    <button
                                                        type="button"
                                                        onClick={onResetFilters}
                                                        className="rounded font-medium text-primary-700 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
                                                    >
                                                        Clear search and filters
                                                    </button>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        emptyMessage
                                    )}
                                </td>
                            </tr>
                        ) : (
                            rows.map((row, idx) => (
                                <tr
                                    key={keyOf(row, idx)}
                                    className={cn(
                                        'hover:bg-slate-50/80 transition-colors',
                                        loading && 'opacity-60',
                                        rowClassName?.(row)
                                    )}
                                >
                                    {columns.map((col) => (
                                        <td
                                            key={col.key}
                                            className={cn(
                                                'px-3 py-1.5 align-middle',
                                                col.align === 'right' && 'text-right',
                                                col.className
                                            )}
                                        >
                                            {col.render ? col.render(row) : row[col.key] ?? '—'}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {pagination && (
                <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 border-t border-slate-200 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                        <span>
                            {from}–{to} of {total}
                        </span>
                        {onLimitChange && (
                            <select
                                value={limit}
                                onChange={(e) => onLimitChange(Number(e.target.value))}
                                aria-label="Rows per page"
                                className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
                            >
                                {PAGE_SIZES.map((n) => (
                                    <option key={n} value={n}>
                                        {n} / page
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <PagerButton label="First page" disabled={page <= 1} onClick={() => onPageChange(1)}>
                            <ChevronsLeft className="h-4 w-4" />
                        </PagerButton>
                        <PagerButton label="Previous page" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
                            <ChevronLeft className="h-4 w-4" />
                        </PagerButton>
                        <span className="px-2 tabular-nums">
                            Page {totalPages === 0 ? 0 : page} of {totalPages}
                        </span>
                        <PagerButton
                            label="Next page"
                            disabled={page >= totalPages}
                            onClick={() => onPageChange(page + 1)}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </PagerButton>
                        <PagerButton
                            label="Last page"
                            disabled={page >= totalPages}
                            onClick={() => onPageChange(totalPages)}
                        >
                            <ChevronsRight className="h-4 w-4" />
                        </PagerButton>
                    </div>
                </div>
            )}
        </div>
    );
};

const PagerButton = ({ label, disabled, onClick, children }) => (
    <Tooltip label={label}>
        <button
            type="button"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            className="h-8 w-8 grid place-content-center rounded-md border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none"
        >
            {children}
        </button>
    </Tooltip>
);

const TONES = {
    default: 'text-slate-600 hover:text-slate-900 hover:bg-slate-100',
    danger: 'text-red-600 hover:text-red-700 hover:bg-red-50',
    success: 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50',
    warning: 'text-amber-600 hover:text-amber-700 hover:bg-amber-50',
    info: 'text-blue-600 hover:text-blue-700 hover:bg-blue-50',
};

/** Icon button for the actions column. `label` is the tooltip and accessible name. */
export const RowAction = ({ icon: Icon, label, onClick, tone = 'default', disabled }) => (
    <Tooltip label={label}>
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
            className={cn(
                'h-7 w-7 grid place-content-center rounded-md transition-colors disabled:opacity-40 disabled:pointer-events-none',
                TONES[tone] || TONES.default
            )}
        >
            <Icon className="h-4 w-4" />
        </button>
    </Tooltip>
);

/** Icon link for the actions column (navigates instead of acting). */
export const RowLink = ({ icon: Icon, label, to }) => (
    <Tooltip label={label}>
        <Link
            to={to}
            aria-label={label}
            className="h-7 w-7 grid place-content-center rounded-md text-slate-600 hover:text-primary-700 hover:bg-primary-50 transition-colors"
        >
            <Icon className="h-4 w-4" />
        </Link>
    </Tooltip>
);

/** Monospace id with a copy-to-clipboard button. */
export const CopyableId = ({ value, label = 'Copy to clipboard', className }) => {
    const [copied, setCopied] = useState(false);
    if (!value) return <span className="text-slate-400">—</span>;
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };
    return (
        <span className={cn('inline-flex items-center gap-1 text-xs text-slate-600', className)}>
            <span className="font-mono truncate max-w-[16rem]" title={value}>
                {value}
            </span>
            <Tooltip label={copied ? 'Copied' : label}>
                <button
                    type="button"
                    onClick={copy}
                    aria-label={label}
                    className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                >
                    {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
            </Tooltip>
        </span>
    );
};

export const RowActions = ({ children }) => <div className="flex items-center justify-end gap-0.5">{children}</div>;

export default DataTable;
